import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getAlert } from '../../shared/api/api';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import useIncidentsByAlertId from '../../shared/hooks/useIncidentsByAlertId';
import './AlertDetailPage.css';

function AlertDetailPage() {
  const { alertId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [alertData, setAlertData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAlert();
  }, [alertId]);

  const fetchAlert = async () => {
    setLoading(true);
    try {
      const response = await getAlert(alertId);
      setAlertData(response.data);
    } catch {
      setError('Failed to load alert details.');
    } finally {
      setLoading(false);
    }
  };

  // CRITICAL/HIGH alerts auto-create an incident server-side (BR-039), so
  // there is nothing to raise manually — but leaving the page with no way to
  // reach the incident that was already created is exactly as unusable as a
  // missing button. Only fetch for the severities that can actually have one.
  const severity = alertData?.severity;
  const incidentsByAlertId = useIncidentsByAlertId(severity === 'CRITICAL' || severity === 'HIGH');
  const linkedIncident = alertData ? incidentsByAlertId.get(alertData.alert_id) || null : null;

  const getSeverityClass = (severity) => {
    switch (severity?.toUpperCase()) {
      case 'CRITICAL': return 'critical';
      case 'HIGH': return 'high';
      case 'MEDIUM': return 'medium';
      case 'LOW': return 'low';
      default: return 'info';
    }
  };

  const getStateClass = (state) => {
    switch (state?.toUpperCase()) {
      case 'ALARM': return 'alarm';
      case 'OK': return 'ok';
      case 'INSUFFICIENT_DATA': return 'insufficient';
      default: return 'unknown';
    }
  };

  if (!loading && !alertData) {
    return (
      <div className="page-container">
        <div className="alert alert-error">{error || 'Alert not found.'}</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {loading && <LoadingSpinner message="Loading alert details..." />}
      <button
        className="btn-back"
        onClick={() => navigate('/alerts', { state: location.state })}
      >
        {'\u2190'} Back to Alerts
      </button>

      {error && <div className="alert alert-error">{error}</div>}

      {alertData && (
        <>
          {/* Header card */}
          <div className="card altd-card">
            <div className="altd-header">
              <div className="altd-header-left">
                <h1 className="altd-title">{alertData.alarm_name}</h1>
                <p className="altd-description">{alertData.alarm_description}</p>
              </div>
              <div className="altd-header-badges">
                <span className={`alerts-severity alerts-severity--${getSeverityClass(alertData.severity)}`}>
                  {alertData.severity}
                </span>
                <span className={`alerts-state alerts-state--${getStateClass(alertData.state)}`}>
                  {alertData.state}
                </span>
                {(alertData.severity === 'MEDIUM' || alertData.severity === 'LOW') && (
                  <button
                    className="btn btn-primary altd-create-incident-btn"
                    onClick={() => navigate(`/incidents/create/from-alert/${alertData.alert_id}`)}
                  >
                    Create Incident
                  </button>
                )}
                {(alertData.severity === 'CRITICAL' || alertData.severity === 'HIGH') && linkedIncident && (
                  <button
                    className="btn btn-primary altd-create-incident-btn"
                    onClick={() => navigate(`/incidents/${linkedIncident.service}/${linkedIncident.incident_id}`)}
                  >
                    View Incident
                  </button>
                )}
              </div>
            </div>

            <div className="altd-fields">
              <DetailField label="Alert ID" value={alertData.alert_id} mono />
              <DetailField label="Service" value={alertData.service} />
              <DetailField label="Environment" value={alertData.environment} />
              <DetailField label="Source" value={alertData.source} />
              <DetailField label="Alert Type" value={alertData.alert_type} />
              <DetailField label="Version" value={alertData.version} />
              <DetailField label="Previous State" value={alertData.previous_state} />
              <DetailField
                label="State Changed"
                value={alertData.state_change_time ? new Date(alertData.state_change_time).toLocaleString() : null}
              />
              <DetailField
                label="Received At"
                value={alertData.received_at ? new Date(alertData.received_at).toLocaleString() : null}
              />
              <DetailField label="Dedup Key" value={alertData.dedup_key} mono />
            </div>
          </div>

          {/* State reason */}
          {alertData.state_reason && (
            <div className="card altd-card">
              <h2 className="section-title">State Reason</h2>
              <p className="altd-state-reason">{alertData.state_reason}</p>
            </div>
          )}

          {/* Metric info */}
          <div className="card altd-card">
            <h2 className="section-title">Metric Details</h2>
            <div className="altd-fields">
              <DetailField label="Metric Name" value={alertData.metric_name} />
              <DetailField label="Namespace" value={alertData.metric_namespace} />
              <DetailField label="Alarm ARN" value={alertData.alarm_arn} mono />
            </div>
            {alertData.dimensions && Object.keys(alertData.dimensions).length > 0 && (
              <div className="altd-dimensions">
                <h3 className="altd-dimensions-title">Dimensions</h3>
                <div className="altd-dimensions-grid">
                  {Object.entries(alertData.dimensions).map(([key, value]) => (
                    <div key={key} className="altd-dimension-item">
                      <span className="altd-dimension-key">{key}</span>
                      <span className="altd-dimension-value">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Teams */}
          {alertData.teams && alertData.teams.length > 0 && (
            <div className="card altd-card">
              <h2 className="section-title">Assigned Teams</h2>
              <div className="altd-teams">
                {alertData.teams.map((team) => (
                  <span key={team} className="alerts-team-badge">{team}</span>
                ))}
              </div>
            </div>
          )}

          {/* Links */}
          {(alertData.runbook_url || alertData.dashboard_url) && (
            <div className="card altd-card">
              <h2 className="section-title">Quick Links</h2>
              <div className="altd-links">
                {alertData.runbook_url && (
                  <a href={alertData.runbook_url} target="_blank" rel="noopener noreferrer" className="altd-link">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                    Runbook
                  </a>
                )}
                {alertData.dashboard_url && (
                  <a href={alertData.dashboard_url} target="_blank" rel="noopener noreferrer" className="altd-link">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>
                    Dashboard
                  </a>
                )}
              </div>
            </div>
          )}
        </>
      )}

    </div>
  );
}

function DetailField({ label, value, mono }) {
  return (
    <div className="altd-field">
      <span className="altd-field-label">{label}</span>
      <span className={`altd-field-value ${mono ? 'altd-field-mono' : ''}`}>
        {value || '\u2014'}
      </span>
    </div>
  );
}

export default AlertDetailPage;
